import React from "react";
import { Lock, Sparkles, Gem, Crown, Warehouse } from "lucide-react";

/**
 * CollectibleChips — restrained, premium signal pills rendered on listing
 * cards, detail pages, and storefront grids. Colour-coded but restrained:
 * we want luxury/curated, not gamified/neon.
 *
 * Rendered chips (when flag truthy):
 *   - VAULTED        (muted brass)   — admin-curated vault inventory
 *   - LIMITED        (deep red)      — scarce release
 *   - EXCLUSIVE      (deep purple)   — platform-exclusive drop
 *   - FOUNDING DROP  (premium gold)  — founding breeder's first live drop
 *
 * Chips wrap cleanly on mobile, remain legible over imagery, and never
 * overpower breeder branding.
 */
const CHIP_STYLES = {
  vault: {
    label: "Vaulted",
    icon: Lock,
    bg: "rgba(139, 115, 85, 0.14)",
    border: "rgba(139, 115, 85, 0.50)",
    color: "#C4A878",
  },
  limited: {
    label: "Limited",
    icon: Sparkles,
    bg: "rgba(139, 58, 58, 0.14)",
    border: "rgba(169, 70, 70, 0.50)",
    color: "#D88585",
  },
  exclusive: {
    label: "Exclusive",
    icon: Gem,
    bg: "rgba(106, 75, 122, 0.16)",
    border: "rgba(138, 100, 158, 0.50)",
    color: "#C8A8D8",
  },
  founding_drop: {
    label: "Founding Drop",
    icon: Crown,
    bg: "rgba(212, 175, 55, 0.10)",
    border: "rgba(212, 175, 55, 0.52)",
    color: "#D4AF37",
  },
  from_house_collection: {
    label: "From the Vault",
    icon: Warehouse,
    bg: "rgba(192, 161, 95, 0.10)",
    border: "rgba(192, 161, 95, 0.55)",
    color: "#C0A15F",
  },
};

// Render order — strongest signal first.
const ORDER = ["founding_drop", "exclusive", "limited", "vault", "from_house_collection"];

export default function CollectibleChips({ listing, size = "sm", testId }) {
  if (!listing) return null;
  const active = ORDER.filter((k) => Boolean(listing[k]));
  if (active.length === 0) return null;

  return (
    <div
      className="flex flex-wrap gap-1.5"
      data-testid={testId || "collectible-chips"}
    >
      {active.map((key) => (
        <Chip key={key} style={CHIP_STYLES[key]} size={size} testKey={key} />
      ))}
    </div>
  );
}

function Chip({ style, size, testKey }) {
  const Icon = style.icon;
  const sizes = size === "xs"
    ? { pad: "px-1.5 py-0.5", text: "text-[9px] tracking-[0.16em]", icon: "w-2.5 h-2.5" }
    : { pad: "px-2 py-1",    text: "text-[10px] tracking-[0.2em]",  icon: "w-3 h-3"   };
  return (
    <span
      className={`inline-flex items-center gap-1 ${sizes.pad} ${sizes.text} font-mono uppercase border`}
      style={{
        background: style.bg,
        borderColor: style.border,
        color: style.color,
      }}
      data-testid={`chip-${testKey}`}
    >
      <Icon className={sizes.icon} />
      {style.label}
    </span>
  );
}
